// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { User } from '@microsoft/microsoft-graph-types';
import { Observable, map, mergeMap, startWith } from 'rxjs';
import { environment } from 'src/environments/environment';
/**
 * this is required so extrernal SENSE PEAK links work
 * otherwise currentWindowIsPupup will return true here:
 * https://github.com/damienbod/angular-auth-oidc-client/blob/main/projects/angular-auth-oidc-client/src/lib/login/popup/popup.service.ts
 * which will cause the login flow to abort
 */
document.defaultView!.opener = undefined;

/**
 * All auth-related APIs needed by the app have to be wrapped here
 * This is important so we can completely mock them in case we want to
 * boot the app in isolation!
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private rawUserData$: Observable<User | undefined> = this.http.get(
    environment.userDataApiUrl,
  );

  userData$: Observable<User | undefined> = this.rawUserData$.pipe(
    map((user) => user),
  );

  userInitials$ = this.userData$.pipe(
    map((u) => {
      return (u?.givenName || '?')[0] + (u?.surname || '?')[0];
    }),
  );

  userProfilePicture$: Observable<string> = this.http
    .get(environment.userProfilePictureApiUrl, {
      responseType: 'blob',
    })
    .pipe(
      mergeMap(async (image) => {
        const imageUrl = await imageDataUrlFromBlob(image);
        return `url('${imageUrl}')`;
      }),
    );

  isAuthenticated$ = this.userData$.pipe(
    startWith(false),
    map((user) => !!user),
  );

  simpleName$ = this.userData$.pipe(
    map((data) => {
      return data ? `${data.givenName}` : '';
    }),
  );

  mailId$ = this.userData$.pipe(
    map((user) => {
      return user?.mail;
    }),
  );

  isAuthorized$: Observable<boolean> = this.rawUserData$.pipe(
    map((user) => {
      const DOMAIN_ID = 'siemens-healthineers.com';
      return !!user?.mail && !!user?.mail.includes(DOMAIN_ID);
    }),
  );

  constructor(
    private msalService: MsalService,
    protected http: HttpClient,
  ) {}

  logout(): void {
    this.msalService.logout();
  }

  getIdToken(): string | undefined {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts[0]?.idToken;
  }
}

function imageDataUrlFromBlob(image: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        resolve(reader.result as string);
      },
      false,
    );

    reader.readAsDataURL(image);
  });
}
